import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {ACCESS_KEY} from 'constants/keys';
import {setAuthHeader} from 'services/api';
import {maxPasswordResetAttempts} from 'constants/DefaultValues';
import {userRole} from 'constants/enums';
const roles = Object.values(userRole);

type AuthData = {
  id: number;
  org_id: number;
  first_name: string;
  last_name: string;
  email: string;
  profile_photo_url: string;
  org_role: (typeof roles)[number];
  is_password_updated: boolean;
};

// Define types for the state
export interface AuthStateType {
  isLoading: boolean;
  successSignin?: string;
  errorSignIn?: string | boolean;
  authData: AuthData | null;
  isAuthenticated: boolean;
  accessToken: string;
  otpTimer: string;
  otpAttempts: number;
  canResetPassword: boolean;
  emailToRecover: string;
}

/**
 * login types
 */

export type loginRequesRequestPayload = {
  email_id: string;
  password: string;
};

export type loginRequesSucessResponse = {
  status_code: string;
  data: AuthData;
  access_token: string;
};

/**
 * types for forgot password
 */

export type resetPasswordOtpRequestPayload = {
  email: string;
};

export type resetPasswordOtpSuccessResponse = {
  status_code: string;
};

/**
 * types for verify OTP
 */

export type verifyOTPRequestPayload = {
  email: string;
  otp: string;
};

export type verifyOTPSuccessResponse = {
  status_code: string;
};

/**
 * types for resend otp
 */
export type resendOTPRequestPayload = {
  email: string;
};

export type resendOTPSuccessResponse = {
  status_code: string;
};

/**
 * types for reset password
 */
export type resetPasswordRequestPayload = {
  current_password: string;
  new_password: string;
};

export type resetPasswordRequestResponse = {
  status_code: string;
};

/**
 * type for set new password
 */
export type setNewPasswordRequestPayload = {
  email: string;
  new_password: string;
};

export type setNewPasswordSuccessResponse = {
  status_code: string;
};

/**
 * type of registering a new orgnization
 */

export type registerOrganizationRequestPayload = {
  org_name: string;
  first_name: string;
  last_name?: string;
  email: string;
};

export type registerOrganizationSuccessResponse = {
  status_code: string;
};

/**
 * type of resending otp for registring organization
 */

export type resendRegisterOrganizationOTPRequestPayload = {
  email: string;
};

export type resendRegisterOrganizationOTPSuccessResponse = {
  status_code: string;
};

/**
 * type of resending otp for registring organization
 */

export type verifyRegisterOrganizationOTPRequestPayload = {
  email: string;
  otp: string;
};

export type verifyRegisterOrganizationOTPSuccessResponse = {
  status_code: string;
  data: Omit<AuthData, 'is_password_updated'>;
  access_token: string;
};

// Initial state with proper types
const initialState: AuthStateType = {
  isLoading: false,
  authData: null,
  isAuthenticated: false,
  accessToken: '',
  errorSignIn: false,
  successSignin: '',
  otpTimer: new Date(1970, 0, 1).toISOString(),
  otpAttempts: maxPasswordResetAttempts,
  // this state will act as a lock to prevent user to go to
  // forgotpassword screen and forgot their password without cliking on forgot password option
  canResetPassword: false,
  emailToRecover: '',
};

// Define the slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Action to request login
    loginRequest(state, action: PayloadAction<loginRequesRequestPayload>) {
      state.isLoading = true;
      state.errorSignIn = false;
    },

    // Action for successful login
    loginSuccess(state, action: PayloadAction<loginRequesSucessResponse>) {
      state.isLoading = false;
      state.authData = action.payload.data;
      state.isAuthenticated = true;
      state.successSignin = action.payload.status_code;
      state.otpAttempts = maxPasswordResetAttempts;
      // set the token
      AsyncStorage.setItem(ACCESS_KEY, action.payload.access_token);
      // manully passing token here since when app is running in memory then the header needs to be set manually
      // it gets automatalli set after a broswer relaod which will not be possible in native apps.
      setAuthHeader(action.payload.access_token);
    },

    // Action for failed login
    loginFailure(state, action: PayloadAction<string | boolean>) {
      state.isLoading = false;
      state.errorSignIn = action.payload;
    },

    resetPasswordOtpRequest(
      state,
      action: PayloadAction<resetPasswordOtpRequestPayload>,
    ) {
      state.isLoading = true;
    },
    resetPasswordOtpSuccess(
      state,
      action: PayloadAction<resetPasswordOtpSuccessResponse>,
    ) {
      state.isLoading = false;
      state.successSignin = action.payload.status_code;
      state.otpAttempts = maxPasswordResetAttempts;
      state.otpTimer = new Date().toISOString();
    },
    resetPasswordOtpFailure(state, action: PayloadAction<string | boolean>) {
      state.isLoading = false;
      state.errorSignIn = action.payload;
    },

    verifyOTPRequest(state, action: PayloadAction<verifyOTPRequestPayload>) {
      state.isLoading = true;
    },
    verifyOTPSuccess(state, action: PayloadAction<verifyOTPSuccessResponse>) {
      state.isLoading = false;
      state.successSignin = action.payload.status_code;
      state.otpAttempts = maxPasswordResetAttempts; // reset the max attemps after a successfull OTP verification
    },
    verifyOTPFailure(state, action: PayloadAction<string | boolean>) {
      state.isLoading = false;
      state.errorSignIn = action.payload;
      if (
        state.otpAttempts > 0 &&
        ['E-10085', 'E-10084', 'E-10010', 'E-10004'].includes(
          action.payload.toString(),
        )
      ) {
        // also check if the error is for OTP error
        state.otpAttempts = state.otpAttempts - 1; // reduce the OTP attemps
      }
      if (action.payload.toString() === 'E-10086') {
        state.otpAttempts = maxPasswordResetAttempts; // reset otp attempts after account block
      }
    },

    resendOTPRequest(state, action: PayloadAction<resendOTPRequestPayload>) {
      state.isLoading = true;
    },
    resendOTPSuccess(state, action: PayloadAction<resendOTPSuccessResponse>) {
      state.isLoading = false;
      state.successSignin = action.payload.status_code;
      if (state.otpAttempts > 0) {
        state.otpTimer = new Date().toISOString(); // reset the timer
        state.otpAttempts = state.otpAttempts - 1; // reduce the OTP attemps
      }
    },
    resendOTPFailure(state, action: PayloadAction<string | boolean>) {
      state.isLoading = false;
      state.errorSignIn = action.payload;
    },

    resetMessage(state) {
      state.errorSignIn = false;
      state.successSignin = '';
    },

    setOtpTimer(state, action: PayloadAction<Date>) {
      // set current time
      state.otpTimer = action.payload.toISOString();
    },

    resetPasswordRequest(
      state,
      action: PayloadAction<resetPasswordRequestPayload>,
    ) {
      state.isLoading = true;
      state.errorSignIn = false;
    },
    resetPasswordSuccess(state, action: PayloadAction<string>) {
      state.successSignin = action.payload;
    },
    resetPasswordFailure(state, action: PayloadAction<string | boolean>) {
      state.isLoading = false;
      state.errorSignIn = action.payload;
    },

    setNewPasswordRequest(
      state,
      action: PayloadAction<setNewPasswordRequestPayload>,
    ) {
      state.isLoading = true;
      state.errorSignIn = false;
      state.successSignin = '';
    },
    setNewPasswordFailure(state, action: PayloadAction<string | boolean>) {
      state.isLoading = false;
      state.errorSignIn = action.payload;
    },
    setNewPasswordSuccess(
      state,
      action: PayloadAction<setNewPasswordSuccessResponse['status_code']>,
    ) {
      state.isLoading = false;
      state.successSignin = action.payload;
    },

    setIsPasswordUpdated(state, action: PayloadAction<boolean>) {
      if (state.authData) {
        state.authData.is_password_updated = action.payload;
      }
    },

    setCanResetPassword(state, action: PayloadAction<boolean>) {
      state.canResetPassword = action.payload;
    },
    setEmailToRecover(state, action: PayloadAction<string>) {
      state.emailToRecover = action.payload;
    },

    setProfilePhotoUrl(state, action: PayloadAction<string>) {
      if (state.authData) {
        state.authData = {...state.authData, profile_photo_url: action.payload};
      }
    },

    registerOrganizationRequest(
      state,
      action: PayloadAction<registerOrganizationRequestPayload>,
    ) {
      state.isLoading = true;
      state.errorSignIn = false;
      state.successSignin = '';
    },

    registerOrganizationFailure(
      state,
      action: PayloadAction<string | boolean>,
    ) {
      state.isLoading = false;
      state.errorSignIn = action.payload;
    },
    registerOrganizationSuccess(
      state,
      action: PayloadAction<registerOrganizationSuccessResponse>,
    ) {
      state.isLoading = false;
      state.successSignin = action.payload.status_code;
      state.otpAttempts = maxPasswordResetAttempts;
      state.otpTimer = new Date().toISOString();
    },

    resendRegisterOrganizationOTPRequest(
      state,
      action: PayloadAction<resendRegisterOrganizationOTPRequestPayload>,
    ) {
      state.isLoading = true;
      state.errorSignIn = false;
      state.successSignin = '';
    },
    resendRegisterOrganizationOTPFailure(
      state,
      action: PayloadAction<string | boolean>,
    ) {
      state.isLoading = false;
      state.errorSignIn = action.payload;
    },
    resendRegisterOrganizationOTPSuccess(
      state,
      action: PayloadAction<resendRegisterOrganizationOTPSuccessResponse>,
    ) {
      state.isLoading = false;
      state.successSignin = action.payload.status_code;

      if (state.otpAttempts > 0) {
        state.otpTimer = new Date().toISOString();
        state.otpAttempts = state.otpAttempts - 1;
      }
    },

    verifyRegisterOrganizationOTPRequest(
      state,
      action: PayloadAction<verifyRegisterOrganizationOTPRequestPayload>,
    ) {
      state.isLoading = true;
      state.errorSignIn = false;
      state.successSignin = '';
    },
    verifyRegisterOrganizationOTPFailure(
      state,
      action: PayloadAction<string | boolean>,
    ) {
      state.isLoading = false;
      state.errorSignIn = action.payload;
      if (
        state.otpAttempts > 0 &&
        ['E-10085', 'E-10084', 'E-10010', 'E-10004'].includes(
          action.payload.toString(),
        )
      ) {
        // also check if the error is for OTP error
        state.otpAttempts = state.otpAttempts - 1; // reduce the OTP attemps
      }
      if (action.payload.toString() === 'E-10086') {
        state.otpAttempts = maxPasswordResetAttempts; // reset otp attempts after account block
      }
    },
    verifyRegisterOrganizationOTPSuccess(
      state,
      action: PayloadAction<verifyRegisterOrganizationOTPSuccessResponse>,
    ) {
      state.isLoading = false;
      state.successSignin = action.payload.status_code;
      state.otpAttempts = maxPasswordResetAttempts;
    },

    resetAuth(state) {
      state.isAuthenticated = false;
      state.authData = null;
      state.isLoading = false;
      state.errorSignIn = false;
      state.successSignin = '';
      AsyncStorage.clear();
    },
  },
});

// Export the actions and reducer
export const {
  loginRequest,
  loginSuccess,
  resetAuth,
  loginFailure,
  resetMessage,
  setOtpTimer,
  resetPasswordOtpFailure,
  resetPasswordOtpRequest,
  resetPasswordOtpSuccess,
  verifyOTPFailure,
  verifyOTPRequest,
  verifyOTPSuccess,
  resendOTPFailure,
  resendOTPRequest,
  resendOTPSuccess,
  resetPasswordFailure,
  resetPasswordRequest,
  resetPasswordSuccess,
  setNewPasswordFailure,
  setNewPasswordRequest,
  setNewPasswordSuccess,
  setIsPasswordUpdated,
  setCanResetPassword,
  setEmailToRecover,
  setProfilePhotoUrl,
  registerOrganizationFailure,
  registerOrganizationRequest,
  registerOrganizationSuccess,
  resendRegisterOrganizationOTPFailure,
  resendRegisterOrganizationOTPRequest,
  resendRegisterOrganizationOTPSuccess,
  verifyRegisterOrganizationOTPFailure,
  verifyRegisterOrganizationOTPRequest,
  verifyRegisterOrganizationOTPSuccess,
} = authSlice.actions;
export default authSlice.reducer;
